// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The rolling ball (M3-24, 07 §3.3).
//
// "The most important continuous sound in the machine, and the one most simulators get wrong."
// Everything else in the audio layer so far is a one-shot: something happened, play a buffer.
// A rolling ball is the opposite — it is a bed that is *always* there while the ball moves,
// and its absence is what makes a simulated table feel like it is happening behind glass in
// another room.
//
// **One looping buffer, moved by the ball.** The buffer is pink noise with a slow amplitude
// modulation baked into it, rendered once at load; each ball gets a source playing that loop
// through a bandpass whose centre follows its speed. That is the whole mechanism, and it costs
// four nodes per ball.
//
// **The playback rate carries the texture, and this is the one non-obvious decision here.**
// 07 asks for amplitude modulation at 30–90 Hz "scaled by speed, emulating wood grain". The
// obvious build is an oscillator into a gain node — two more nodes per ball and a second
// parameter to keep in step. But a ball crossing the same grain faster gets a *faster* flutter
// **and** a brighter noise, together and in exactly the same proportion, because both come
// from the same physical rate. Baking the modulation into the loop and varying `playbackRate`
// reproduces that with the node that was already there: rate 1 gives 30 Hz, rate 3 gives 90.
// Coupling them is not a shortcut around two parameters — two independent parameters would be
// the way to get it wrong.
//
// **The gate matters more than it looks.** Below 0.15 m/s the voice is silent, because a ball
// resting in a cradle is not rolling, and a bed that never quite stops is a hum the player
// cannot identify and cannot ignore.

import type { LayerKind } from '@core/machine'
import { Rng } from '@core/rng/Rng'
import {
  addSweptNoise,
  fadeOut,
  filterInPlace,
  loopSeamless,
  normalise,
  OnePole,
  samplesFor,
  type NoiseSource,
  type Samples,
} from './dsp'
import { noiseFrom } from './solenoids'
import { LoopVoice, type HeadAt } from './loopVoice'
import type { Spot } from './space'
import { VoicePool, type VoiceHandle } from './voicePool'
import {
  decibelsToGain,
  type AudioBufferLike,
  type AudioNodeLike,
  type AudioPlaybackContext,
} from './webAudio'

// --- the loop ----------------------------------------------------------------------------

/** Seconds of pink noise in the loop. Long enough that a listener never hears the seam. */
export const LOOP_SECONDS = 2

/** Modulation rate baked in, Hz. `playbackRate` 1…3 turns this into the 30–90 Hz of 07 §3.3. */
export const AM_BASE_HZ = 30

/** How deep the modulation goes. Full depth is a helicopter; none is a hiss. */
export const AM_DEPTH = 0.35

/** Cross-fade at the loop seam, milliseconds. */
const SEAM_MS = 60

/**
 * Pink noise by the Paul Kellet economical filter — six one-pole sections over white.
 *
 * Pink rather than white because rolling noise is broadband with a downward tilt: white noise
 * bandpassed still sounds like escaping steam, and no amount of filtering afterwards puts the
 * missing low-frequency weight back. The coefficients are the published ones, so a reader who
 * knows the filter recognises them rather than having to trust them, and the C# core can copy
 * six lines rather than re-derive a spectrum.
 */
export class PinkNoise implements NoiseSource {
  private b0 = 0
  private b1 = 0
  private b2 = 0
  private b3 = 0
  private b4 = 0
  private b5 = 0
  private b6 = 0

  constructor(private readonly white: NoiseSource) {}

  next(): number {
    const w = this.white.next()
    this.b0 = 0.99886 * this.b0 + w * 0.0555179
    this.b1 = 0.99332 * this.b1 + w * 0.0750759
    this.b2 = 0.969 * this.b2 + w * 0.153852
    this.b3 = 0.8665 * this.b3 + w * 0.3104856
    this.b4 = 0.55 * this.b4 + w * 0.5329522
    this.b5 = -0.7616 * this.b5 - w * 0.016898
    const out = this.b0 + this.b1 + this.b2 + this.b3 + this.b4 + this.b5 + this.b6 + w * 0.5362
    this.b6 = w * 0.115926
    // The filter's own gain is about 3.5; scaling here keeps the buffer near unity before the
    // normalise, so a change to the section count does not silently change the bed's level.
    return out * 0.11
  }
}

/**
 * Render the rolling loop.
 *
 * Seamless by folding: `SEAM_MS` of extra signal is rendered past the end and cross-faded back
 * over the head, so the sample that follows the last one is the one the generator actually
 * produced next. A loop cut with a hard edge ticks once every two seconds, which is both
 * quiet enough to survive review and obvious enough to be the only thing a player hears.
 *
 * The modulation phase is chosen so a whole number of cycles fits the kept length. Otherwise
 * the flutter jumps phase at the seam — a defect the fold cannot hide, because it is in the
 * envelope rather than in the waveform.
 */
export function renderRollingLoop(sampleRate: number, noise: NoiseSource): Samples {
  const cycles = Math.max(1, Math.round(LOOP_SECONDS * AM_BASE_HZ))
  const length = Math.max(2, Math.round((cycles * sampleRate) / AM_BASE_HZ))
  const seam = Math.min(length - 1, samplesFor(SEAM_MS, sampleRate))

  const pink = new PinkNoise(noise)
  const raw = new Float32Array(length + seam)

  // The modulation as a rotating phasor rather than a `Math.sin` per sample — see the note at
  // the top of `dsp.ts`. This is the longest single loop in the whole synthesis layer, two
  // seconds against a coil's fiftieth of one.
  const step = (2 * Math.PI * AM_BASE_HZ) / sampleRate
  const cosStep = Math.cos(step)
  const sinStep = Math.sin(step)
  let c = 1
  let s = 0

  for (let i = 0; i < raw.length; i++) {
    raw[i] = pink.next() * (1 - AM_DEPTH + AM_DEPTH * (0.5 + 0.5 * s))
    const next = c * cosStep - s * sinStep
    s = s * cosStep + c * sinStep
    c = next
  }

  const out = loopSeamless(raw, length, seam)
  normalise(out)
  return out
}

// --- the drain ------------------------------------------------------------------------------

/**
 * The drain (07 §3.3): "a hollow descending roll into the trough, then the trough kick."
 *
 * The descent is a **resonance** falling, not a pitch. There is no tone in a ball rolling down
 * a wooden channel — what changes as it disappears under the playfield is the size of the space
 * it is heard through, and that is a bandpass sliding down over the same broadband rattle. A
 * pitch drop here would sound like a cartoon.
 *
 * It matters out of proportion to its length. A drain is the worst thing that happens in a game
 * of pinball and the machine should acknowledge it — a ball that simply vanishes reads as the
 * simulation losing track of it rather than as the player losing the ball.
 */
export const DRAIN_FROM_HZ = 620
export const DRAIN_TO_HZ = 130
export const DRAIN_Q = 2.2
export const DRAIN_SWEEP_MS = 380
export const DRAIN_DECAY_MS = 320

/** Under the playfield, behind a sheet of plywood — the same lowpass the trough coil gets. */
export const DRAIN_MUFFLE_HZ = 1400

export function drainLengthMs(): number {
  return DRAIN_DECAY_MS * 1.15 + 8
}

export function renderDrainRoll(
  sampleRate: number,
  noise: NoiseSource,
  pitchScale = 1,
): Samples {
  const out = new Float32Array(samplesFor(drainLengthMs(), sampleRate))
  addSweptNoise(
    out,
    sampleRate,
    new PinkNoise(noise),
    DRAIN_FROM_HZ * pitchScale,
    DRAIN_TO_HZ * pitchScale,
    DRAIN_Q,
    1,
    DRAIN_DECAY_MS,
    DRAIN_SWEEP_MS,
  )
  filterInPlace(out, new OnePole(DRAIN_MUFFLE_HZ, sampleRate))
  normalise(out)
  fadeOut(out, sampleRate)
  return out
}

// --- what a surface does to it -------------------------------------------------------------

export type RollingSurface = 'wood' | 'ramp' | 'wire'

/**
 * The three surfaces of 07 §3.3, as multipliers on the wood case.
 *
 * `rate` is the extra flutter: a plastic ramp is smooth, so it flutters less than wood; a wire
 * habitrail is two thin rails with a gap between them and rattles far more than either.
 */
export interface SurfaceVoicing {
  /** Multiplier on the centre frequency. */
  readonly centre: number
  readonly q: number
  /** Multiplier on `playbackRate`, which is the modulation rate. */
  readonly rate: number
  /** Level relative to the wood bed, dB. */
  readonly db: number
}

export const ROLLING_SURFACES: Readonly<Record<RollingSurface, SurfaceVoicing>> = {
  // The playfield: warm and broad, and the reference every other surface is described against.
  wood: { centre: 1, q: 1.4, rate: 1, db: 0 },
  // A plastic ramp: hollow and resonant. Higher and much narrower, which is what "hollow"
  // is — a tube with a formant rather than a panel with a spectrum — and louder, because a
  // ramp is a sounding board with nothing under it to damp it.
  ramp: { centre: 1.35, q: 3.2, rate: 0.8, db: 2 },
  // A wire habitrail: thin and rattling. Very high, very narrow, and with the flutter well up,
  // because the ball is crossing two rails rather than one surface.
  wire: { centre: 2.6, q: 6, rate: 1.6, db: -1 },
}

/** Which of the three a simulation layer is. A queue is a trough — wood, and under a panel. */
export function surfaceForLayer(kind: LayerKind): RollingSurface {
  if (kind === 'ramp') return 'ramp'
  if (kind === 'wire') return 'wire'
  return 'wood'
}

// --- the mapping from speed ------------------------------------------------------------------

/** Below this the ball is not rolling, whatever the solver says it is touching (07 §3.3). */
export const ROLL_GATE_SPEED = 0.15

/** The speed at which the mapping tops out. A ball off a flipper is about this. */
export const ROLL_REFERENCE_SPEED = 6

/** `centre = 180 + 420 · (speed / 6)`, straight from 07 §3.3. */
export const ROLL_BASE_HZ = 180
export const ROLL_SPAN_HZ = 420

/** Level of the bed at full speed, dB. Under everything: it is a bed, not an event. */
export const ROLL_FULL_DB = -16

/** Fastest the loop is played, and therefore 90 Hz of flutter at the top of the range. */
export const ROLL_MAX_RATE = 3

/** Fraction of full speed, 0…1. */
function fraction(speed: number): number {
  const s = Math.abs(speed) / ROLL_REFERENCE_SPEED
  return s > 1 ? 1 : s
}

export function rollingCentreHz(speed: number, surface: RollingSurface): number {
  return (ROLL_BASE_HZ + ROLL_SPAN_HZ * fraction(speed)) * ROLLING_SURFACES[surface].centre
}

/**
 * Bed level, 0…1. Proportional to speed, gated, and zero below the gate.
 *
 * Proportional rather than perceptual on purpose: 07 says proportional, and a ball drifting at
 * a tenth of its top speed really is nearly inaudible against a machine that is also playing
 * coils. A square-root curve here makes a slow ball sound like a fast one.
 */
export function rollingGain(speed: number, surface: RollingSurface): number {
  const s = Math.abs(speed)
  if (s < ROLL_GATE_SPEED) return 0
  return fraction(s) * decibelsToGain(ROLL_FULL_DB + ROLLING_SURFACES[surface].db)
}

/** Playback rate, which is the modulation rate: 1 at rest, 3 at the reference speed. */
export function rollingRate(speed: number, surface: RollingSurface): number {
  const rate = 1 + (ROLL_MAX_RATE - 1) * fraction(speed) * ROLLING_SURFACES[surface].rate
  return rate < 0.25 ? 0.25 : rate
}

// --- the runtime bed --------------------------------------------------------------------------

/** The rolling loop, uploaded once and shared by every ball. */
export function rollingBuffer(context: AudioPlaybackContext, seed: number): AudioBufferLike {
  const samples = renderRollingLoop(context.sampleRate, noiseFrom(new Rng(seed)))
  const buffer = context.createBuffer(1, samples.length, context.sampleRate)
  buffer.getChannelData(0).set(samples)
  return buffer
}

/**
 * Steps a ball's bed waits after being stolen before it asks for a slot again.
 *
 * Without it two rolling balls in a full pool take the same slot from each other on alternate
 * frames for as long as they both roll, which costs a node teardown and rebuild sixty times a
 * second and sounds like a bed being switched on and off. Half a second is long enough that
 * whatever stole the slot has finished, and short enough that a ball still rolling gets its
 * bed back before the player notices it went.
 */
export const STEAL_COOLDOWN_STEPS = 500

/**
 * Steps a bed keeps playing after the last step the ball was in contact.
 *
 * Found by measurement, not by reasoning: three balls of real play rebuilt the bed **thirty-six
 * times**, and each rebuild fades the bed out and back in over a glide. A ball rolling across a
 * playfield is not in continuous contact — it chatters, and it leaves the surface for a few
 * milliseconds at every seam and every small bounce. Sampling `touchingSurface` once a frame
 * catches whichever step the frame happened to end on, so the bed flickers on a ball that is
 * plainly rolling.
 *
 * Ninety milliseconds bridges a bounce and a chatter, and is short enough that a genuine
 * airball — a third of a second at least, or it did not clear anything — still goes silent. It
 * is a step count rather than a frame count, so a machine at 30 fps and one at 120 hold the bed
 * for the same length of time.
 */
export const ROLL_HOLD_STEPS = 90

/**
 * Every ball's bed, and the pool arbitration around it.
 *
 * The beds go through `VoicePool` at `ballRoll` priority, which is the whole reason that rank
 * exists (07 §2): during a six-ball frenzy something has to lose, and a continuous bed nobody
 * is listening *for* is the right thing to lose to a jackpot stab. Held outside the pool they
 * could never be stolen, and the pool would deny the stab instead — exactly backwards.
 */
export class RollingBed {
  private readonly voices: LoopVoice[] = []
  private readonly handles: (VoiceHandle | undefined)[] = []
  private readonly retryAt: number[] = []
  /** Last step each ball was reported in contact. Drives `ROLL_HOLD_STEPS`. */
  private readonly touchedAt: number[] = []
  /**
   * The most recent step any ball was updated at.
   *
   * The steal callback fires whenever the pool decides, which is not the frame that acquired the
   * slot — so a cool-down measured from the acquiring frame's step would already be part spent,
   * and for a bed that had been rolling for a while, entirely spent.
   */
  private lastStep = 0

  /** Beds started. A number that climbs faster than the balls do means the hold is too short. */
  started = 0

  /** Beds stolen for something more important. A steady climb means the pool is too small. */
  stolen = 0

  constructor(
    private readonly context: AudioPlaybackContext,
    private readonly buffer: AudioBufferLike,
    private readonly destination: AudioNodeLike,
    private readonly pool: VoicePool,
    /**
     * Where the listener is, asked once a frame (M9-48).
     *
     * The default is "there is no head", so a bed built without one is the stereo collapse every
     * bed in this product was before M9-48 — and a rolling ball is the sound that most wants the
     * other half, because it is the one emitter that crosses the whole playfield while it plays.
     */
    private readonly head: HeadAt = () => undefined,
  ) {}

  /** How many balls are audibly rolling. */
  get playing(): number {
    let n = 0
    for (const voice of this.voices) if (voice.playing) n++
    return n
  }

  /**
   * One ball, one frame. `step` is the simulation's, because there is no clock here either.
   *
   * `touching` is whether the ball had a surface under it on the last step the caller saw. It is
   * held for `ROLL_HOLD_STEPS` rather than believed frame by frame — see the constant.
   */
  update(
    ball: number,
    speed: number,
    surface: RollingSurface,
    /**
     * Where the ball is (M3-30, 07 §8), not just which side of the table.
     *
     * A pan alone made every ball the same distance away, on a playfield two and a half times
     * longer than it is wide — so a ball idling at the top of the table rolled as loudly as one
     * about to drain. The distance term is applied to the level the surface and the speed have
     * already decided, which is the order the physical thing happens in.
     */
    at: Spot,
    step: number,
    touching: boolean,
  ): void {
    const voice = this.voiceFor(ball)
    this.lastStep = step
    if (touching) this.touchedAt[ball] = step
    const last = this.touchedAt[ball]
    const recent = last !== undefined && step - last <= ROLL_HOLD_STEPS
    const wanted = recent && rollingGain(speed, surface) > 0

    if (!wanted) {
      this.silence(ball)
      return
    }

    if (this.handles[ball] === undefined) {
      if (step < (this.retryAt[ball] ?? 0)) return
      const handle = this.pool.acquire('ballRoll', () => {
        // The pool is telling us the slot is gone. Drop the nodes and stand off, rather than
        // asking for it straight back on the next frame.
        this.handles[ball] = undefined
        this.retryAt[ball] = this.lastStep + STEAL_COOLDOWN_STEPS
        this.stolen++
        voice.stop()
      })
      if (handle === undefined) return
      this.handles[ball] = handle
      this.started++
    }

    voice.aim({
      level: rollingGain(speed, surface) * decibelsToGain(at.db),
      pan: at.pan,
      rate: rollingRate(speed, surface),
      centreHz: rollingCentreHz(speed, surface),
      q: ROLLING_SURFACES[surface].q,
      // The ball's own point, re-aimed every frame. `pan`/`db` above stay the flat collapse of
      // it, so a laptop hears exactly what it heard before M9-48 and a headset hears the ball
      // travel up the table.
      at,
    })
  }

  /**
   * Stop one ball's bed and give its slot back.
   *
   * Does not clear the contact stamp: a bed silenced because the ball slowed below the gate
   * should come straight back when it speeds up again, and only a ball that has genuinely left
   * the surface for `ROLL_HOLD_STEPS` should have to earn its bed again.
   */
  silence(ball: number): void {
    const handle = this.handles[ball]
    if (handle !== undefined) {
      this.handles[ball] = undefined
      this.pool.release(handle)
    }
    this.voices[ball]?.stop()
  }

  /** Stop everything, as when the machine is rebuilt or the audio is torn down. */
  stopAll(): void {
    for (let i = 0; i < this.voices.length; i++) this.silence(i)
    this.touchedAt.length = 0
  }

  /**
   * A new game has begun at step 0. Everything here that is a step number belongs to the previous
   * game: a steal cooldown set near step 250,000 would otherwise keep a ball silent for minutes.
   */
  newGame(): void {
    this.stopAll()
    this.retryAt.length = 0
    this.lastStep = 0
  }

  private voiceFor(ball: number): LoopVoice {
    let voice = this.voices[ball]
    if (voice === undefined) {
      // Filtered: the bed's centre frequency *is* the sound, and it moves with the ball's speed.
      voice = new LoopVoice(this.context, this.buffer, this.destination, true, this.head)
      this.voices[ball] = voice
      this.retryAt[ball] = 0
    }
    return voice
  }
}
